import { useEffect, useMemo, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";
import type {
  RouteLine,
  RouteLineDayData,
} from "../../../../../types/RouteLine/RouteLine";
import { useThemeContext } from "../../../../theme/ThemeContext";
import { Theme } from "../../../../../constant/Theme";
import { useRouteLineContext } from "../../../../../context/MapViews/RouteLine/RouteLineContext";

interface SelectedRouteLinesViewProps {
  routeLines: RouteLine[];
  selectedRouteLine: RouteLine | null;
}

const DIRECTION_LABELS: Record<string, string> = {
  i: "Inbound",
  o: "Outbound",
};

const directionLabel = (direction: string | null): string =>
  direction ? (DIRECTION_LABELS[direction.toLowerCase()] ?? direction) : "—";

const getOperatorNames = (routeLines: RouteLine[]): string[] => {
  const names = routeLines
    .map((rl) => rl.operatorNa)
    .filter((name): name is string => name !== null);
  return Array.from(new Set(names));
};

const buildDirectionOptions = (
  routeLines: RouteLine[],
): { index: number; label: string }[] => {
  const directionCounts = new Map<string, number>();
  for (const rl of routeLines) {
    const key = directionLabel(rl.direction);
    directionCounts.set(key, (directionCounts.get(key) ?? 0) + 1);
  }
  const directionCounters = new Map<string, number>();
  return routeLines.map((rl, index) => {
    const base = directionLabel(rl.direction);
    const total = directionCounts.get(base) ?? 1;
    const counter = (directionCounters.get(base) ?? 0) + 1;
    directionCounters.set(base, counter);
    const label = total > 1 ? `${base} ${counter}` : base;
    return { index, label };
  });
};

const PERIOD_LABELS: { key: keyof RouteLineDayData; label: string }[] = [
  { key: "early", label: "Early 04:00 - 06:59" },
  { key: "am", label: "AM 07:00 - 08:59" },
  { key: "bp", label: "BP 09:00 - 15:59" },
  { key: "ep", label: "EP 16:00 - 17:59" },
  { key: "op", label: "OP 18:00 - 23:59" },
  { key: "night", label: "Night 00:00 - 02:59" },
];

const DayDataTable = ({ dayData }: { dayData: RouteLineDayData }) => (
  <Table striped bordered size="sm" className="mb-0 text-center">
    <thead>
      <tr>
        <th>Period</th>
        <th>Frequency</th>
      </tr>
    </thead>
    <tbody>
      {PERIOD_LABELS.map(({ key, label }) => (
        <tr key={key}>
          <td>{label}</td>
          <td>{dayData[key] as number}</td>
        </tr>
      ))}
    </tbody>
  </Table>
);

const getDaysForRouteLine = (routeLine: RouteLine): string[] =>
  routeLine.dayData.map((dd) => dd.dayOfWeek);

const SelectedRouteLinesView = ({
  routeLines,
  selectedRouteLine,
}: SelectedRouteLinesViewProps) => {
  const { theme } = useThemeContext();

  const unselectedDayVariant =
    theme === Theme.LIGHT ? "outline-dark" : "outline-light";
  const operatorNames = useMemo(
    () => getOperatorNames(routeLines),
    [routeLines],
  );

  const isDisabled = operatorNames.length <= 1;

  const [selectedOperator, setSelectedOperator] = useState<string>(
    () => operatorNames[0] ?? "",
  );

  // Reset selectedOperator when routeLines changes and the current operator is no longer valid.
  useEffect(() => {
    setSelectedOperator((prev) =>
      operatorNames.includes(prev) ? prev : (operatorNames[0] ?? ""),
    );
  }, [operatorNames]);

  const filteredRouteLines = useMemo(
    () => routeLines.filter((rl) => rl.operatorNa === selectedOperator),
    [routeLines, selectedOperator],
  );

  const directionOptions = useMemo(() => {
    const opts = buildDirectionOptions(filteredRouteLines);
    // Remap indices to point into the full routeLines array so onChange and value stay consistent.
    return opts.map((opt, i) => ({
      label: opt.label,
      index: routeLines.indexOf(filteredRouteLines[i]),
    }));
  }, [filteredRouteLines, routeLines]);

  const { setSelectedRouteLine } = useRouteLineContext();

  const availableDays = useMemo(
    () => (selectedRouteLine ? getDaysForRouteLine(selectedRouteLine) : []),
    [selectedRouteLine],
  );

  const [selectedDay, setSelectedDay] = useState<string>(
    () => availableDays[0] ?? "",
  );

  useEffect(() => {
    setSelectedDay(availableDays[0] ?? "");
  }, [availableDays]);

  const selectedDayData = useMemo(
    () =>
      selectedRouteLine?.dayData.find((d) => d.dayOfWeek === selectedDay) ??
      null,
    [selectedRouteLine, selectedDay],
  );

  return (
    <div className="p-2 d-flex flex-column gap-4">
      <div className="d-flex gap-2 flex-column">
        <h5 className="text-black">{`Service Number: ${selectedRouteLine?.serviceNum}`}</h5>
        <Form.Select
          size="sm"
          disabled={isDisabled}
          value={selectedOperator}
          onChange={(e) => setSelectedOperator(e.target.value)}
          aria-label="Select operator"
        >
          {operatorNames.map((name) => (
            <option key={name} value={name}>
              {name}
            </option>
          ))}
        </Form.Select>
        <Form.Select
          size="sm"
          disabled={directionOptions.length <= 1}
          value={routeLines.indexOf(selectedRouteLine as RouteLine)}
          onChange={(e) =>
            setSelectedRouteLine(routeLines[Number(e.target.value)])
          }
          aria-label="Select direction"
        >
          {directionOptions.map(({ index, label }) => (
            <option key={index} value={index}>
              {label}
            </option>
          ))}
        </Form.Select>
      </div>
      <div className="d-flex gap-2 flex-column">
        <div className="d-flex gap-1">
          {availableDays.map((day) => (
            <Button
              key={day}
              size="sm"
              variant={selectedDay === day ? "primary" : unselectedDayVariant}
              onClick={() => setSelectedDay(day)}
            >
              {day}
            </Button>
          ))}
        </div>
        {selectedDayData && <DayDataTable dayData={selectedDayData} />}
      </div>
    </div>
  );
};

export default SelectedRouteLinesView;
